import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { panelFrameStyle } from "../../../shared/ui/card-frame";
import { pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { activitiesApi } from "../../activities/api/activities.api";
import { activityKeys } from "../../activities/constants/query-keys";
import { activityHrefParts } from "../../activities/lib/activity-href";
import { useAllBookingDrafts, type NamedBookingDraft } from "../../activities/lib/booking-draft";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

/**
 * "You were booking" at the top of the home page (round 18, item 146).
 *
 * A guest who left a booking at the details step and came back to the home
 * page found nothing there that remembered it: the half-done booking lived
 * only on the Booking tab. This card shows the newest one with the choices
 * already made, and Finish booking reopens the sheet where they left off
 * (`?book=1`; the sheet restores its own draft, step included).
 */

const RADIUS = 20;
const FRAME = panelFrameStyle(RADIUS);
const INNER = pewterInner(solid("#FFFFFF"), RADIUS);
/** Per tab: a card the guest closed stays closed until that draft changes. */
const DISMISSED_KEY = "mj-home-resume-dismissed";

/** The step the sheet is on: package, when, who, details, review. */
const DETAILS_STEP = 3;

interface DraftActivity {
  /** The API sends the city as an object; `citySlug` is often null. */
  city?: { slug?: string | null } | null;
  title?: string;
  cityName?: string;
  citySlug?: string | null;
  category?: { slug?: string | null } | null;
  mode?: string;
  segment?: string | null;
  images?: string[];
  packages?: Array<{ id: string; name: string }>;
}

/** A draft worth offering: started for real, and not for a day already gone. */
export function resumableDraft(drafts: NamedBookingDraft[], today: string): NamedBookingDraft | null {
  return drafts.find((d) => d.step > 0 && (!d.date || d.date >= today)) ?? null;
}

const dismissalOf = (d: NamedBookingDraft) => `${d.slug}:${d.savedAt}`;

/** Today on the Moroccan calendar, YYYY-MM-DD. */
function moroccoToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Africa/Casablanca" }).format(now);
}

export function ResumeBookingCard() {
  // Drafts live in this browser only: the server never renders the card, so
  // it waits for mount rather than disagree with the hydrating page.
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  useEffect(() => {
    setMounted(true);
    try {
      setDismissed(window.sessionStorage.getItem(DISMISSED_KEY));
    } catch {
      /* private mode: nothing remembered, nothing lost */
    }
  }, []);
  const drafts = useAllBookingDrafts();
  const draft = mounted ? resumableDraft(drafts, moroccoToday()) : null;
  if (!draft || dismissed === dismissalOf(draft)) return null;
  return (
    <ResumeCard
      draft={draft}
      onDismiss={() => {
        const key = dismissalOf(draft);
        setDismissed(key);
        try {
          window.sessionStorage.setItem(DISMISSED_KEY, key);
        } catch {
          /* ignore */
        }
      }}
    />
  );
}

function ResumeCard({ draft, onDismiss }: { draft: NamedBookingDraft; onDismiss: () => void }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const locale = lang ?? "en";
  const { data } = useQuery({
    queryKey: activityKeys.detail(draft.slug, locale),
    queryFn: () => activitiesApi.getBySlug(draft.slug, locale),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
  const a = (data?.data ?? null) as DraftActivity | null;
  // Wait for the title: a card naming a slug reads as broken.
  if (!a?.title) return null;

  const link = activityHrefParts({
    lang: locale,
    citySlug: a.citySlug ?? a.city?.slug ?? null,
    categorySlug: a.category?.slug ?? null,
    segment: a.mode === "explore" ? (a.segment ?? null) : null,
    slug: draft.slug,
  });
  const pkgName = a.packages?.find((p) => p.id === draft.pkgId)?.name;
  const day = draft.date
    ? new Intl.DateTimeFormat(i18n.language, {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      }).format(new Date(`${draft.date}T12:00:00Z`))
    : null;
  // The package and the day; the party is left out, since a table and an
  // activity count children differently in the same draft fields.
  const facts = [pkgName, day].filter(Boolean);

  return (
    <section className="mx-auto w-full max-w-[1320px] px-4 pt-3 sm:px-6 lg:px-8" data-testid="resume-booking">
      <div className="mj-fade-in max-w-[560px]" style={FRAME}>
        <div className="relative p-3" style={INNER}>
          <div className="flex items-center justify-between">
            <p className="font-sans text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#5a5d77]">
              {t("home.resume_eyebrow", { defaultValue: "You were booking" })}
            </p>
            <button
              type="button"
              onClick={onDismiss}
              aria-label={t("home.resume_hide", { defaultValue: "Hide" })}
              className="grid h-7 w-7 place-items-center rounded-full text-[#5a5d77]"
            >
              <Icon name="ri-close-line" className="text-[15px]" aria-hidden />
            </button>
          </div>
          <div className="mt-1 flex items-center gap-3">
            <div className="h-14 w-14 shrink-0 overflow-hidden rounded-[14px] bg-[#f4f1ff]">
              {a.images?.[0] ? (
                <ImageWithFallback src={a.images[0]} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : null}
            </div>
            <div className="min-w-0 flex-1">
              <p className="line-clamp-2 break-words font-heading text-[15px] font-bold text-[#110E39]">{a.title}</p>
              {facts.length > 0 && (
                <p className="line-clamp-2 font-sans text-[12px] font-medium text-[#5a5d77]">{facts.join(" · ")}</p>
              )}
              <p className="font-sans text-[12px] font-semibold text-[#8A6425]">
                {draft.step >= DETAILS_STEP
                  ? t("home.resume_details_left", { defaultValue: "Only your details left" })
                  : t("home.resume_pick_up", { defaultValue: "Pick up where you left off" })}
              </p>
            </div>
          </div>
          <Link
            to={link.to}
            params={link.params}
            search={{ book: 1 } as never}
            className="mj-press mt-3 flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-full font-sans text-[13.5px] font-semibold text-white no-underline"
            style={{ background: BEVEL_NAVY_FACE, boxShadow: BEVEL_INSET_NAVY }}
          >
            {t("home.resume_cta", { defaultValue: "Finish booking" })}
            <Icon name="ri-arrow-right-s-line" className="text-[16px] rtl:rotate-180" aria-hidden />
          </Link>
        </div>
      </div>
    </section>
  );
}
